/**
 * MobileCqcReadiness — orchestrator for the mobile CQC Readiness view
 * (Batch 7A). Rendered from Dashboard.jsx under `lg:hidden`.
 *
 * Zero backend changes. Every action goes through the same handlers the
 * desktop tree already wires. Sub-tab pills: Summary · Areas · Actions ·
 * History.
 */
import React, { useState } from "react";
import { LayoutDashboard, ClipboardList, ListChecks, History } from "lucide-react";
import MobileReadinessSummary from "@/components/mobile/MobileReadinessSummary";
import MobileReadinessAreasList from "@/components/mobile/MobileReadinessAreasList";
import MobileReadinessActionsList from "@/components/mobile/MobileReadinessActionsList";
import MobileReadinessSnapshotsList from "@/components/mobile/MobileReadinessSnapshotsList";

const TABS = [
  { key: "summary",   label: "Summary", Icon: LayoutDashboard },
  { key: "areas",     label: "Areas",   Icon: ClipboardList },
  { key: "actions",   label: "Actions", Icon: ListChecks },
  { key: "history",   label: "History", Icon: History },
];

export default function MobileCqcReadiness({
  readiness, latestSignoff, signoffHistory, readinessActions, staffList, canManage,
  onExportPdf, onBoardReviewPdf, onOpenSignDialog, onDownloadLatestPdf,
  onUpsertAction, onOpenActionHistory, onDownloadSnapshotPdf,
}) {
  const [tab, setTab] = useState("summary");
  if (!readiness) return null;

  return (
    <section data-testid="mobile-readiness-section" className="space-y-3">
      {/* Sub-tab pills */}
      <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-1" data-testid="mobile-readiness-tabs">
        {TABS.map(({ key, label, Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`shrink-0 h-11 min-h-[44px] px-3 rounded-full border text-[12px] font-medium inline-flex items-center gap-1.5 ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300"
              }`}
              data-testid={`mobile-readiness-tab-${key}`}
              aria-pressed={active}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {key === "actions" && readiness.recommendations?.length > 0 && (
                <span className={`ml-1 tabular-nums text-[10px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100"}`}>
                  {readiness.recommendations.length}
                </span>
              )}
              {key === "history" && signoffHistory?.length > 0 && (
                <span className={`ml-1 tabular-nums text-[10px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100"}`}>
                  {signoffHistory.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "summary" && (
        <MobileReadinessSummary
          readiness={readiness}
          latestSignoff={latestSignoff}
          canManage={canManage}
          onExportPdf={onExportPdf}
          onBoardReviewPdf={onBoardReviewPdf}
          onOpenSignDialog={onOpenSignDialog}
          onDownloadLatestPdf={onDownloadLatestPdf}
        />
      )}
      {tab === "areas" && (
        <MobileReadinessAreasList
          breakdown={readiness.breakdown}
          evidence_readiness={readiness.evidence_readiness}
        />
      )}
      {tab === "actions" && (
        <MobileReadinessActionsList
          recommendations={readiness.recommendations}
          readinessActions={readinessActions}
          staffList={staffList}
          canManage={canManage}
          onUpsert={onUpsertAction}
          onOpenHistory={onOpenActionHistory}
        />
      )}
      {tab === "history" && (
        <MobileReadinessSnapshotsList
          signoffHistory={signoffHistory}
          onDownloadPdf={onDownloadSnapshotPdf}
        />
      )}
    </section>
  );
}
